import { DirectiveBinding } from "vue";

export type ClickOutsideElement = Element & {
  __vueClickOutsideEventHandler__: EventListener;
};

/**
 * Allows for an action to happen when a click is registered outside of the
 * component this directive is used on.
 *
 * Ex. closing a DatePicker when a click occurs outside it.
 */
export const vClickOutside = {
  beforeMount: function (
    el: ClickOutsideElement,
    binding: DirectiveBinding,
  ): void {
    function clickEventHandler(event: Event): void {
      if (!el.contains(event.target as Node) && el !== event.target) {
        binding.value(event);
      }
    }

    // store the handler so we can refer to it later
    el.__vueClickOutsideEventHandler__ = clickEventHandler;

    // attach handler as listener
    document.addEventListener("click", clickEventHandler, true);
  },
  unmounted: function (el: ClickOutsideElement): void {
    document.removeEventListener("click", el.__vueClickOutsideEventHandler__);
  },
};

interface VDisableBinding {
  condition: boolean;
  enabledClass: string;
  disabledClass: string;
}
function enable(
  el: Element,
  enabledClass: string[],
  disabledClass: string[],
): void {
  el.setAttribute("tabindex", "0");
  el.classList.remove("pointer-events-none", ...disabledClass);
  el.classList.add(...enabledClass);
}
function disable(
  el: Element,
  enabledClass: string[],
  disabledClass: string[],
): void {
  el.setAttribute("tabindex", "-1");
  el.classList.add("pointer-events-none", ...disabledClass);
  el.classList.remove(...enabledClass);
}
export function vDisable(
  el: Element,
  binding: DirectiveBinding<VDisableBinding>,
): void {
  // Makes element unclickable and unfocusable by keyboard (useful for disabling <router-link>)
  const e = binding.value.enabledClass.split(" ");
  const d = binding.value.disabledClass.split(" ");
  if (binding.value.condition) disable(el, e, d);
  else enable(el, e, d);
}

let isScrolling = false;
function applyScroll(
  element: Element,
  target: Element,
  modifiers: { x?: boolean; y?: boolean },
): void {
  if (modifiers.x) {
    target.scroll({
      left: element.scrollLeft,
      behavior: "auto",
    });
  }
  if (modifiers.y) {
    target.scroll({
      top: element.scrollLeft,
      behavior: "auto",
    });
  }
}
interface ScrollSyncGroup {
  modifiers: { x?: boolean; y?: boolean };
  e: Element;
}

const groups: Record<string, ScrollSyncGroup[]> = {};
function elements(
  group: string,
  modifiers?: { x?: boolean; y?: boolean },
  e?: Element,
): ScrollSyncGroup[] {
  if (e) {
    if (!(group in groups)) {
      groups[group] = [];
    }
    groups[group].push({ modifiers: modifiers ?? {}, e });
  }
  return groups[group];
}

// Heavily inspired by https://github.com/imarc/v-scroll-sync/blob/master/v-scroll-sync.js
/**
 * You can group together divs to sync scrolling with `<div v-scroll-sync>`
 * Optionally, you may provide multiple groups: `<div v-scroll-sync:group1>`
 * You may also specify which axis to sync: `<div v-scroll-sync.x>`
 *  By default both axis are synced
 */
export const vScrollSync = {
  beforeMount: function (el: Element, binding: DirectiveBinding): void {
    let modifiers = {
      x: binding.modifiers.x ?? false,
      y: binding.modifiers.y ?? false,
    };
    if (!(modifiers.x || modifiers.y)) {
      modifiers = { x: true, y: true };
    }

    elements(binding.arg ?? "group", modifiers, el);
    el.addEventListener("scroll", () => {
      if (!isScrolling) {
        isScrolling = true;
        requestAnimationFrame(() => {
          elements(binding.arg ?? "group")
            .filter((obj) => obj.e !== el)
            .forEach((o) => {
              applyScroll(el, o.e, o.modifiers);
            });
        });

        requestAnimationFrame(() => {
          isScrolling = false;
        });
      }
    });
  },
};
